// Operations Snapshot Types and Utilities for ReadyMix Concrete
import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export enum ProductionStatusLevel {
    OnTrack = 'OnTrack',
    MinorConcerns = 'MinorConcerns',
    CriticalIssues = 'CriticalIssues',
}

export interface AlertItem {
    id: string;
    message: string;
    severity: 'warning' | 'error' | 'info';
    link?: string;
}

export interface PlantStatus {
    plantId: string;
    plantName: string;
    operationalStatus: 'operational' | 'maintenance' | 'offline';
    hourlyCapacityYards: number;
}

export interface FleetStatus {
    available: number;
    onDelivery: number;
    maintenance: number;
    outOfService: number;
    total: number;
}

export interface LoadsByStatus {
    scheduled: number;
    batching: number;
    loading: number;
    inTransit: number;
    onSite: number;
    discharging: number;
    completed: number;
}

export interface OperationsSnapshotData {
    overallStatus: {
        level: ProductionStatusLevel;
        message: string;
    };
    dailyProduction: {
        actualYards: number;
        targetYards: number;
    };
    activeLoads: LoadsByStatus;
    fleetStatus: FleetStatus;
    plantStatuses: PlantStatus[];
    activeAlerts: AlertItem[];
    qualityMetrics?: {
        slumpTestsPassed: number;
        slumpTestsTotal: number;
        rejectionsToday: number;
    };
}

export const getStatusIconAndColor = (level: ProductionStatusLevel) => {
    switch (level) {
        case ProductionStatusLevel.OnTrack:
            return { icon: <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />, color: 'success.main', chipColor: 'success' as 'success' | 'warning' | 'error' };
        case ProductionStatusLevel.MinorConcerns:
            return { icon: <WarningAmberIcon sx={{ fontSize: 30 }} />, color: 'warning.main', chipColor: 'warning' as 'success' | 'warning' | 'error' };
        case ProductionStatusLevel.CriticalIssues:
            return { icon: <ErrorOutlineIcon sx={{ fontSize: 30 }} />, color: 'error.main', chipColor: 'error' as 'success' | 'warning' | 'error' };
        default:
            return { icon: <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />, color: 'text.secondary', chipColor: 'default' as 'success' | 'warning' | 'error' | 'default' };
    }
};

export const getAlertIcon = (severity: 'warning' | 'error' | 'info') => {
    switch (severity) {
        case 'error': return <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />;
        case 'warning': return <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />;
        default: return <CheckCircleOutlineIcon color="info" sx={{ fontSize: 16 }} />;
    }
};

export const getPlantStatusColor = (status: 'operational' | 'maintenance' | 'offline') => {
    switch (status) {
        case 'operational': return 'success.main';
        case 'maintenance': return 'warning.main';
        case 'offline': return 'error.main';
        default: return 'text.secondary';
    }
};
